import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Award, ShieldCheck, Calendar, GraduationCap, ExternalLink,
  Download, Copy, CheckCircle, Star, Trophy, Share2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const SOCIAL_PLATFORMS = [
  { name: 'TikTok', color: 'bg-black hover:bg-gray-800 text-white', icon: () => <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .55.04.81.11V9a6.27 6.27 0 00-.81-.05A6.34 6.34 0 003.15 15.3a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.78a8.18 8.18 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.21z"/></svg> },
  { name: 'Instagram', color: 'bg-gradient-to-tr from-amber-500 via-pink-500 to-purple-600 hover:opacity-90 text-white', icon: () => <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
  { name: 'Snapchat', color: 'bg-yellow-400 hover:bg-yellow-500 text-black', icon: () => <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12.879-.207.06-.024.12-.045.18-.06.098-.03.18-.045.27-.045a.72.72 0 01.48.18c.105.104.194.27.194.479 0 .463-.51.659-1.109.896-.03.014-.06.03-.09.044l-.39.18a.96.96 0 00-.54.735c-.015.105-.015.21 0 .315.015.09.03.18.06.27.21.555.54 1.065.96 1.485.63.63 1.44 1.065 2.37 1.275.21.046.36.225.36.42 0 .36-.27.615-.57.675-.6.135-1.26.3-1.665.555a.96.96 0 00-.36.39c-.09.18-.12.375-.09.555.03.195.15.345.3.465.18.135.39.21.63.21.21 0 .45-.06.69-.12.24-.06.48-.12.72-.12.255 0 .48.075.66.225.18.15.285.375.285.63 0 .87-.93 1.335-1.71 1.545-1.14.3-2.49.45-3.72.45-1.23 0-2.58-.15-3.72-.45-.78-.21-1.71-.675-1.71-1.545 0-.255.105-.48.285-.63.18-.15.405-.225.66-.225.24 0 .48.06.72.12.24.06.48.12.69.12.24 0 .45-.075.63-.21.15-.12.27-.27.3-.465.03-.18 0-.375-.09-.555a.96.96 0 00-.36-.39c-.405-.255-1.065-.42-1.665-.555-.3-.06-.57-.315-.57-.675 0-.195.15-.375.36-.42.93-.21 1.74-.645 2.37-1.275.42-.42.75-.93.96-1.485.03-.09.045-.18.06-.27.015-.105.015-.21 0-.315a.96.96 0 00-.54-.735l-.39-.18c-.03-.015-.06-.03-.09-.045-.6-.24-1.11-.435-1.11-.9 0-.21.09-.375.195-.48a.72.72 0 01.48-.18c.09 0 .18.015.27.045.06.015.12.036.18.06.22.087.579.207.879.207.198 0 .326-.045.4-.09a8.57 8.57 0 01-.03-.51l-.002-.06c-.105-1.628-.23-3.654.3-4.847C7.86 1.069 11.216.793 12.206.793z"/></svg> },
];

const MILESTONE_ICONS = {
  'STEM Explorer': Star,
  'Category Pioneer': GraduationCap,
  'Knowledge Builder': Trophy,
  'STEM Scholar': GraduationCap,
  'STEM Champion': Trophy,
};

export default function CertificatesPage() {
  const { user, api } = useAuth();
  const [learners, setLearners] = useState([]);
  const [selectedLearnerId, setSelectedLearnerId] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [milestoneAwards, setMilestoneAwards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLearners = async () => {
      try {
        const res = await api.get('/learners/');
        const list = res.data.results || res.data;
        setLearners(list);
        if (list.length > 0) setSelectedLearnerId(list[0].id);
      } catch (e) {
        console.error('Error fetching learners:', e);
      }
    };
    fetchLearners();
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedLearnerId) return;
    setLoading(true);
    try {
      const [certsRes, msRes] = await Promise.all([
        api.get(`/platform/certificates/learner/${selectedLearnerId}/`),
        api.get(`/platform/certificates/milestones/${selectedLearnerId}/`),
      ]);
      setCertificates(certsRes.data.certificates || []);
      setMilestoneAwards(msRes.data.awards || []);
    } catch (e) {
      console.error('Error fetching certificates:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedLearnerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyVerificationLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Verification link copied!');
  };

  const downloadPdf = (certId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/platform/certificates/pdf/${certId}/`;
    window.open(url, '_blank');
  };

  const [sharingCertId, setSharingCertId] = useState(null);

  const downloadStoryCard = async (certId) => {
    setSharingCertId(certId);
    try {
      const res = await api.get(`/platform/certificates/story/${certId}/`);
      const { image_base64, filename } = res.data;
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${image_base64}`;
      link.download = filename;
      link.click();
      toast.success('Story card downloaded! Share it on TikTok, Instagram, or Snapchat!');
    } catch {
      toast.error('Failed to generate story card');
    } finally {
      setSharingCertId(null);
    }
  };

  const gradeColor = (grade) => {
    if (grade === 'A+') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (grade === 'A') return 'bg-blue-100 text-blue-700 border-blue-200';
    if (grade === 'B+') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    if (grade === 'B') return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading && certificates.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="certificates-loading">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="certificates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-600" />
            My Certificates
          </h1>
          <p className="text-sm text-gray-500 mt-1">Blockchain-verified STEM achievement certificates</p>
        </div>
      </div>

      {/* Learner Selector */}
      {learners.length > 1 && (
        <div className="flex items-center gap-3" data-testid="learner-selector">
          <span className="text-sm font-medium text-muted-foreground">Learner:</span>
          <div className="flex gap-2">
            {learners.map(l => (
              <button
                key={l.id}
                onClick={() => setSelectedLearnerId(l.id)}
                data-testid={`learner-tab-${l.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedLearnerId === l.id
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-indigo-300'
                }`}
              >
                {l.first_name} {l.last_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Certificates Grid */}
      {certificates.length === 0 ? (
        <Card className="border-dashed border-2" data-testid="no-certificates">
          <CardContent className="p-12 text-center">
            <Award className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-700 mb-2">No Certificates Yet</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Complete STEM milestones on the Readiness Score dashboard to earn verified certificates.
              Each milestone unlocks a unique certificate with blockchain verification.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="certificates-grid">
          {certificates.map((cert) => {
            const Icon = MILESTONE_ICONS[cert.track] || Award;
            return (
              <Card key={cert.cert_id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`cert-card-${cert.cert_id}`}>
                <div className="h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500" />
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{cert.track}</h3>
                        <p className="text-xs text-gray-400 font-mono">{cert.cert_id}</p>
                      </div>
                    </div>
                    <Badge className={gradeColor(cert.grade)}>
                      Grade {cert.grade}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(cert.issued_at).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <Award className="h-3.5 w-3.5" />
                      Score: {cert.score}%
                    </div>
                  </div>

                  {/* Verification Status */}
                  <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg mb-4">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    <span className="text-xs font-medium text-emerald-700">
                      {cert.verification_status?.valid ? 'Verified & Authentic' : 'Verification Pending'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadPdf(cert.cert_id)}
                        className="flex-1"
                        data-testid={`cert-pdf-${cert.cert_id}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
                      </Button>
                      {cert.verification_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyVerificationLink(cert.verification_url)}
                          data-testid={`cert-copy-${cert.cert_id}`}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    {/* Social Share Buttons */}
                    <div className="flex gap-1.5" data-testid={`cert-social-share-${cert.cert_id}`}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadStoryCard(cert.cert_id)}
                        disabled={sharingCertId === cert.cert_id}
                        className="flex-1 border-purple-200 text-purple-600 hover:bg-purple-50 text-xs"
                        data-testid={`cert-download-story-${cert.cert_id}`}
                      >
                        {sharingCertId === cert.cert_id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                        ) : (
                          <Download className="h-3.5 w-3.5 mr-1" />
                        )}
                        Story Card
                      </Button>
                      {SOCIAL_PLATFORMS.map(({ name, color, icon: Icon }) => (
                        <Button
                          key={name}
                          size="sm"
                          onClick={() => {
                            downloadStoryCard(cert.cert_id);
                            toast.success(`Story card downloading! Share it on ${name}!`);
                          }}
                          className={`${color} text-xs px-2`}
                          data-testid={`cert-share-${name.toLowerCase()}-${cert.cert_id}`}
                        >
                          <Icon />
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Milestone Awards Section */}
      {milestoneAwards.length > 0 && (
        <div data-testid="milestone-awards-section">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Milestone Awards
          </h2>
          <div className="space-y-3">
            {milestoneAwards.map((award) => (
              <div key={award.id} className="flex items-center gap-4 p-4 bg-white rounded-xl border hover:shadow-sm transition-shadow" data-testid={`milestone-award-${award.milestone_id}`}>
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">{award.milestone_name}</h4>
                  <p className="text-xs text-gray-500">
                    Awarded {new Date(award.awarded_at).toLocaleDateString()}
                    {award.certificate_id && ` | Cert: ${award.certificate_id}`}
                  </p>
                </div>
                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                  +{award.points_awarded} pts
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
